import { HTTP_API_V2 } from '../../http-common'

// Logical capability flag of the Inobram Platform feature and the experimental
// attributes that gate experimental features. A device only exposes an
// experimental feature when its active subscription plan carries the Platform
// feature with the matching attribute turned on (experimental plan).
const PLATFORM_FEATURE_FLAG = 'platform'
const ALARMS_ATTRIBUTE_KEY = 'alarms'
const CORRELATION_CHARTS_ATTRIBUTE_KEY = 'correlation_charts'
const BATCH_INSIGHTS_ATTRIBUTE_KEY = 'batch_insights'
const COMFORT_METRICS_ATTRIBUTE_KEY = 'comfort_metrics'
const BIRD_WEIGHT_ATTRIBUTE_KEY = 'bird_weight'

function isAttributeEnabled (value) {
  return String(value).trim().toLowerCase() === 'true'
}

function deviceHasAttributeEnabled (deviceFeatures, attributeKey) {
  const features = (deviceFeatures && deviceFeatures.features) || []
  return features.some(feature => {
    if (feature.feature_flag !== PLATFORM_FEATURE_FLAG) {
      return false
    }
    const attributes = feature.attributes || []
    return attributes.some(attribute =>
      attribute.key === attributeKey && isAttributeEnabled(attribute.value)
    )
  })
}

function enabledDeviceIds (state, attributeKey) {
  return state.loadedDeviceFeaturesV2
    .filter(deviceFeatures => deviceHasAttributeEnabled(deviceFeatures, attributeKey))
    .map(deviceFeatures => deviceFeatures.deviceId)
}

export default {
  state: {
    loadedDeviceFeaturesV2: []
  },
  getters: {
    loadedDeviceFeaturesV2 (state) {
      return state.loadedDeviceFeaturesV2
    },
    // Device ids whose active plan enables the experimental alarms feature.
    alarmEnabledDeviceIds (state) {
      return enabledDeviceIds(state, ALARMS_ATTRIBUTE_KEY)
    },
    // True when the current user has at least one device with alarms enabled.
    hasAlarmsFeature (state, getters) {
      return getters.alarmEnabledDeviceIds.length > 0
    },
    // Device ids whose active plan enables the experimental correlation charts.
    correlationChartsEnabledDeviceIds (state) {
      return enabledDeviceIds(state, CORRELATION_CHARTS_ATTRIBUTE_KEY)
    },
    // True when the current user has at least one device with correlation charts enabled.
    hasCorrelationChartsFeature (state, getters) {
      return getters.correlationChartsEnabledDeviceIds.length > 0
    },
    // Device ids whose active plan enables the experimental batch insights.
    batchInsightsEnabledDeviceIds (state) {
      return enabledDeviceIds(state, BATCH_INSIGHTS_ATTRIBUTE_KEY)
    },
    // True when the current user has at least one device with batch insights enabled.
    hasBatchInsightsFeature (state, getters) {
      return getters.batchInsightsEnabledDeviceIds.length > 0
    },
    // Device ids whose active plan enables the experimental comfort metrics
    // (card metric/comfort-index customization).
    comfortMetricsEnabledDeviceIds (state) {
      return enabledDeviceIds(state, COMFORT_METRICS_ATTRIBUTE_KEY)
    },
    // True when the current user has at least one device with comfort metrics enabled.
    hasComfortMetricsFeature (state, getters) {
      return getters.comfortMetricsEnabledDeviceIds.length > 0
    },
    // Device ids whose active plan enables the experimental bird weight screen
    // (weighing history + weight analysis and projection).
    birdWeightEnabledDeviceIds (state) {
      return enabledDeviceIds(state, BIRD_WEIGHT_ATTRIBUTE_KEY)
    },
    // True when the current user has at least one device with bird weight enabled.
    hasBirdWeightFeature (state, getters) {
      return getters.birdWeightEnabledDeviceIds.length > 0
    }
  },
  mutations: {
    setLoadedDeviceFeaturesV2 (state, payload) {
      state.loadedDeviceFeaturesV2 = payload
    },
    clearLoadedDeviceFeaturesV2 (state) {
      state.loadedDeviceFeaturesV2 = []
    }
  },
  actions: {
    loadDeviceFeaturesV2 ({ commit }) {
      return HTTP_API_V2.get('devices/features')
        .then(result => {
          const deviceFeatures = Array.isArray(result.data) ? result.data : []
          commit('setLoadedDeviceFeaturesV2', deviceFeatures)
          return deviceFeatures
        })
        .catch(err => {
          // A failure here must not break the app; it simply leaves the user
          // without experimental features (alarms hidden).
          console.log(err)
          commit('clearLoadedDeviceFeaturesV2', [])
          return []
        })
    }
  }
}



// WEBPACK FOOTER //
// ./src/store/device_features/index.js